import React, { useState, useEffect } from 'react';
import { 
    TextField, 
    Button, 
    Container, 
    List, 
    ListItem, 
    ListItemText, 
    Typography, 
    Paper,
    Divider,
    Link,
    Box,
    Chip,
    Stack
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { userRequest } from '../../requestMethods';
import AddIcon from '@mui/icons-material/Add';
import PageContainer from '../layout/PageContainer';

const GrammarTopicsList = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const handleSearch = async () => {
        if (searchQuery.trim() === '') {
            // If search is empty, fetch all topics
            fetchAllTopics();
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await userRequest.get(`/grammar/tags/${searchQuery}`);
            setTopics(response.data);
        } catch (err) {
            console.error('Error searching grammar topics:', err);
            setError('Failed to search grammar topics. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllTopics = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await userRequest.get('/grammar');
            console.log('response', response.data);
            setTopics(response.data);
        } catch (err) {
            console.error('Error fetching grammar topics:', err);
            setError('Failed to fetch grammar topics. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Load all topics when component mounts
        fetchAllTopics();
    }, []);

    return (
        <PageContainer maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 3 }}>
                    Grammar Topics
                </Typography>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/grammar/create')}
                >
                    Create Topic
                </Button>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <TextField 
                    label="Search Grammar Topics" 
                    fullWidth
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearch();
                        }
                    }}
                    sx={{ mb: 2 }}
                />
                <Button 
                    variant="contained" 
                    onClick={handleSearch}
                    disabled={loading}
                >
                    Search
                </Button>
            </Paper>

            {loading && <Typography>Loading topics...</Typography>}
            
            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {!loading && topics.length === 0 && (
                <Typography sx={{ mb: 2 }}>
                    No grammar topics found. Try a different search term.
                </Typography>
            )}

            {topics.length > 0 && (
                <Paper elevation={2}>
                    <List>
                        {topics.map((topic, index) => (
                            <React.Fragment key={topic._id || index}>
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Box>
                                                <Link 
                                                    component={RouterLink} 
                                                    to={`/grammar/topic/${topic.slug}`}
                                                    underline="hover"
                                                    color="primary"
                                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                                >
                                                    {topic.title}
                                                </Link>
                                                {(topic.language || topic.level) && (
                                                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                        {topic.language && (
                                                            <Chip 
                                                                label={topic.language.name} 
                                                                size="small" 
                                                                variant="outlined" 
                                                                color="primary"
                                                            />
                                                        )}
                                                        {topic.level && (
                                                            <Chip 
                                                                label={topic.level.name} 
                                                                size="small" 
                                                                variant="outlined" 
                                                                color="secondary"
                                                            />
                                                        )}
                                                    </Stack>
                                                )}
                                            </Box>
                                        }
                                        secondary={topic.summary || 'No summary available'}
                                    />
                                </ListItem>
                                {index < topics.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>
            )}

            <div style={{ height: '60px' }}></div>
        </PageContainer>
    );
};

export default GrammarTopicsList;
